import type { SupabaseClient } from "@supabase/supabase-js"

import { usdPerUnitFromUsdBaseRates, type UsdPerUnitMap } from "@/lib/currency-convert"
import { frankfurterLatestFromUsdUrl } from "@/lib/frankfurter-url"

type RateRow = {
  base_currency: string
  quote_currency: string
  rate: number
}

function rowsToUsdPerUnit(rows: RateRow[]): UsdPerUnitMap | null {
  const fromQuotes = rows.filter((r) => r.base_currency === "USD" && r.quote_currency !== "USD")
  if (fromQuotes.length === 0) return null
  const rates: Record<string, number> = {}
  for (const r of fromQuotes) {
    rates[r.quote_currency] = r.rate
  }
  return usdPerUnitFromUsdBaseRates(rates)
}

/** Loads cached FX from Supabase, or fetches full Frankfurter `latest?from=USD` when empty. */
export async function loadUsdPerUnitFromDbOrFrankfurter(client: SupabaseClient): Promise<UsdPerUnitMap> {
  const { data, error } = await client
    .from("currency_exchange_rates")
    .select("base_currency, quote_currency, rate")
    .eq("base_currency", "USD")

  if (!error && data && data.length > 1) {
    const map = rowsToUsdPerUnit(data as RateRow[])
    if (map && Object.keys(map).length > 1) return map
  }

  const res = await fetch(frankfurterLatestFromUsdUrl())
  if (!res.ok) throw new Error(`Frankfurter HTTP ${res.status}`)
  const body = (await res.json()) as { rates?: Record<string, number> }
  return usdPerUnitFromUsdBaseRates(body.rates ?? {})
}
